/**
 * "Chia sẻ" on a finished photo (both modes): the JPEG itself through the device's share sheet — Zalo, Messenger… on
 * a phone — with the Web Share API; no Zalo / Facebook SDK. Shown only where the browser can share files
 * (`navigator.canShare({ files })`); elsewhere "Tải" is enough. The file is fetched when the button appears: Safari
 * opens the share sheet only straight from the tap, not after a download started by it.
 */
import { useEffect, useRef, useState } from "react";
import { SHARE_RESULT } from "../messages";

/** True when this browser can share a JPEG file (false in most desktop browsers and in tests). */
export function canShareFiles(): boolean {
  try {
    if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function") return false;
    return navigator.canShare({ files: [new File([new Uint8Array([0xff, 0xd8])], "anh.jpg", { type: "image/jpeg" })] });
  } catch {
    return false;
  }
}

async function fetchFile(url: string, name: string): Promise<File> {
  const resp = await fetch(url);                       // a signed link (works in <img src>): no session needed
  if (!resp.ok) throw new Error(`file ${resp.status}`);
  const blob = await resp.blob();
  return new File([blob], name, { type: blob.type || "image/jpeg" });
}

interface Props {
  /** the photo's signed link (api.fileUrl(job.files.photo)) */
  url: string;
  /** the file name the friend receives: "anh-the-1a2b3c4d.jpg" */
  name: string;
}

export function ShareResult({ url, name }: Props) {
  const [supported] = useState(canShareFiles);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const pending = useRef<Promise<File | null> | null>(null);
  const link = useRef(url);
  link.current = url;

  useEffect(() => {                                    // once per photo (a new signed link for it changes nothing)
    if (!supported) return;
    let live = true;
    setFile(null);
    setFailed(false);
    const p = fetchFile(link.current, name).catch((e) => {
      console.warn("photo to share unavailable", e);
      return null;
    });
    pending.current = p;
    void p.then((f) => live && setFile(f));
    return () => {
      live = false;
    };
  }, [supported, name]);

  if (!supported) return null;

  async function share() {
    setFailed(false);
    let f = file;
    if (!f) {                                          // still downloading (or failed: one more try)
      setBusy(true);
      f = await (pending.current ?? Promise.resolve(null))
        .then((got) => got ?? fetchFile(link.current, name))
        .catch(() => null);
      setBusy(false);
      if (f) setFile(f);
    }
    if (!f || !navigator.canShare({ files: [f] })) {
      setFailed(true);
      return;
    }
    try {
      await navigator.share({ files: [f], title: SHARE_RESULT.title });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return; // the user closed the share sheet
      console.warn("share failed", e);
      setFailed(true);
    }
  }

  return (
    <>
      <button type="button" className="btn" onClick={() => void share()} disabled={busy}>
        {busy ? SHARE_RESULT.busy : SHARE_RESULT.button}
      </button>
      {failed && <p role="alert" className="error share-failed">{SHARE_RESULT.failed}</p>}
    </>
  );
}
